import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, BadgeCheck } from 'lucide-react';
// ============================================================================
// Types
// ============================================================================
interface ScannedProduct {
  title: string;
  editionLabel?: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl?: string;
  weight?: number; // for weighted random selection
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
// ============================================================================
// Chilean buyer data
// ============================================================================
const CHILEAN_NAMES = ['Marcela T.', 'Patricia R.', 'Carla M.', 'Sonia L.', 'Andrea V.', 'Carolina P.', 'María José S.', 'Pamela G.', 'Verónica D.', 'Daniela H.', 'Camila F.', 'Francisca A.', 'Paula B.', 'Valentina O.', 'Constanza Z.', 'Javiera N.', 'Catalina E.', 'Rocío C.', 'Macarena J.', 'Pilar Q.', 'Soledad I.', 'Magdalena U.', 'Bárbara K.', 'Loreto W.', 'Antonia Y.', 'Claudia X.', 'Mónica L.', 'Ana Luisa M.', 'Isidora T.', 'Trinidad R.'];
const CHILEAN_CITIES = ['Santiago', 'Concepción', 'Valparaíso', 'Viña del Mar', 'Puerto Montt', 'Antofagasta', 'La Serena', 'Temuco', 'Iquique', 'Rancagua', 'Talca', 'Chillán', 'Osorno', 'Punta Arenas', 'Arica', 'Calama', 'Valdivia', 'Copiapó', 'Curicó', 'Quilpué', 'Coquimbo', 'Los Ángeles', 'Quillota', 'San Antonio', 'Ovalle', 'Linares', 'Melipilla', 'Padre Hurtado', 'Coyhaique', 'Castro'];
// ============================================================================
// Product scanner — tries to detect product from DOM, falls back to known data
// ============================================================================
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'Método Fuego Cero',
  editionLabel: 'Método Anti-Hinchazón',
  priceNumber: 6390,
  priceText: '$6.390 CLP',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/qnXyCi8Wh4bwVuRp1MBxsU/mockup.png',
  weight: 1
}];
function scanPageForProducts(): ScannedProduct[] {
  if (typeof document === 'undefined') return FALLBACK_PRODUCTS;
  try {
    const products: ScannedProduct[] = [];
    // 1. Try JSON-LD Product schema
    const ldScripts = document.querySelectorAll('script[type="application/ld+json"]');
    ldScripts.forEach(s => {
      try {
        const data = JSON.parse(s.textContent || '');
        const items = Array.isArray(data) ? data : [data];
        items.forEach((item: any) => {
          if (item['@type'] === 'Product' && item.offers) {
            const offer = Array.isArray(item.offers) ? item.offers[0] : item.offers;
            const priceNumber = parseFloat(offer.price);
            if (!isNaN(priceNumber)) {
              products.push({
                title: item.name || 'Producto',
                priceNumber,
                priceText: `$${priceNumber.toLocaleString('es-CL')} ${offer.priceCurrency || 'CLP'}`,
                thumbnailUrl: Array.isArray(item.image) ? item.image[0] : item.image
              });
            }
          }
        });
      } catch {
        /* ignore parse errors */
      }
    });
    if (products.length > 0) return products;
    // 2. Look for CLP price patterns in the page
    const priceRegex = /\$\s?([0-9]{1,3}(?:[.,][0-9]{3})*(?:[.,][0-9]+)?)\s*CLP/gi;
    const bodyText = document.body.innerText;
    const matches = Array.from(bodyText.matchAll(priceRegex));
    if (matches.length > 0) {
      // Take the most prominent (often largest) price; assume Chilean format $X.XXX
      const priceStrings = matches.map(m => m[1]);
      // Parse "6.390" → 6390 (Chilean format uses dot as thousands separator)
      const numbers = priceStrings.map(p => {
        const cleaned = p.replace(/[.,]/g, '');
        return parseInt(cleaned, 10);
      }).filter(n => !isNaN(n) && n > 100);
      if (numbers.length > 0) {
        // Use the lowest current price (after discount usually)
        const minPrice = Math.min(...numbers);
        // Try to find a heading near the price
        const h1 = document.querySelector('h1');
        const title = h1?.textContent?.split(':')[0]?.trim() || 'Producto';
        const img = document.querySelector('img[alt*="ebook" i], img[alt*="producto" i], img[alt*="cover" i], main img') as HTMLImageElement | null;
        products.push({
          title,
          editionLabel: 'Método Anti-Hinchazón',
          priceNumber: minPrice,
          priceText: `$${minPrice.toLocaleString('es-CL')} CLP`,
          thumbnailUrl: img?.src
        });
      }
    }
    return products.length > 0 ? products : FALLBACK_PRODUCTS;
  } catch {
    return FALLBACK_PRODUCTS;
  }
}
// ============================================================================
// Helpers
// ============================================================================
function pickWeighted<T extends {
  weight?: number;
}>(items: T[]): T {
  if (items.length === 1) return items[0];
  const totalWeight = items.reduce((sum, it) => sum + (it.weight ?? 1), 0);
  let r = Math.random() * totalWeight;
  for (const item of items) {
    r -= item.weight ?? 1;
    if (r <= 0) return item;
  }
  return items[items.length - 1];
}
function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}
function pickNameAvoidingRecent(names: string[], recent: string[]): string {
  const available = names.filter(n => !recent.includes(n));
  if (available.length === 0) return pickRandom(names);
  return pickRandom(available);
}
function randomInterval(minMs: number, maxMs: number): number {
  return Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;
}
// ============================================================================
// Component
// ============================================================================
export function PurchaseNotifications() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const productsRef = useRef<ScannedProduct[]>(FALLBACK_PRODUCTS);
  const recentNamesRef = useRef<string[]>([]);
  const toastIdRef = useRef(0);
  const timeoutsRef = useRef<number[]>([]);
  const prefersReducedMotionRef = useRef<boolean>(false);
  useEffect(() => {
    // Detect reduced motion preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      prefersReducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    // Scan page after a tiny delay so all content has rendered
    const scanTimeout = window.setTimeout(() => {
      productsRef.current = scanPageForProducts();
    }, 500);
    timeoutsRef.current.push(scanTimeout);
    const showToast = () => {
      const product = pickWeighted(productsRef.current);
      const name = pickNameAvoidingRecent(CHILEAN_NAMES, recentNamesRef.current);
      const city = pickRandom(CHILEAN_CITIES);
      // Update recent name history (rolling 10)
      recentNamesRef.current = [name, ...recentNamesRef.current].slice(0, 10);
      const newToast: ToastData = {
        id: ++toastIdRef.current,
        name,
        city,
        product
      };
      setToast(newToast);
      // Hide after 4s
      const hideTimeout = window.setTimeout(() => {
        setToast(null);
        // Schedule next toast 8-15s later
        const nextTimeout = window.setTimeout(showToast, randomInterval(8000, 15000));
        timeoutsRef.current.push(nextTimeout);
      }, 4000);
      timeoutsRef.current.push(hideTimeout);
    };
    // First toast after 3s
    const firstTimeout = window.setTimeout(showToast, 3000);
    timeoutsRef.current.push(firstTimeout);
    return () => {
      timeoutsRef.current.forEach(t => clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, []);
  const reduced = prefersReducedMotionRef.current;
  return <div aria-live="polite" aria-atomic="true" className="fixed left-1/2 -translate-x-1/2 bottom-4 sm:bottom-6 z-[80] pointer-events-none w-full px-4 sm:px-0 sm:w-auto flex justify-center" data-id="element-211">
      <AnimatePresence mode="wait" data-id="element-212">
        {toast && <motion.div key={toast.id} initial={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: reduced ? 0.15 : 0.4,
        ease: 'easeOut'
      }} role="status" className="pointer-events-auto bg-white border border-brand-navy/10 shadow-2xl rounded-2xl px-4 py-3 flex items-center gap-3 max-w-sm w-full sm:w-auto" data-id="element-213">
            {toast.product.thumbnailUrl ? <div className="relative shrink-0" data-id="element-214">
                <img src={toast.product.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover bg-brand-cream" data-id="element-215" />
                <div className="absolute -bottom-1 -right-1 bg-brand-green rounded-full p-0.5 shadow" data-id="element-216">
                  <ShoppingBag className="w-3 h-3 text-white" data-id="element-217" />
                </div>
              </div> : <div className="w-12 h-12 rounded-lg bg-brand-cream flex items-center justify-center shrink-0" data-id="element-218">
                <ShoppingBag className="w-5 h-5 text-brand-gold" data-id="element-219" />
              </div>}

            <div className="flex-1 min-w-0" data-id="element-220">
              <p className="text-sm text-brand-navy leading-tight" data-id="element-221">
                <span className="font-bold" data-id="element-222">{toast.name}</span>
                <span className="text-brand-navy/70" data-id="element-223"> de </span>
                <span className="font-medium" data-id="element-224">{toast.city}</span>
              </p>
              <p className="text-xs text-brand-navy/70 mt-0.5 truncate" data-id="element-225">
                acaba de comprar{' '}
                <span className="font-semibold text-brand-navy" data-id="element-226">
                  {toast.product.editionLabel || toast.product.title}
                </span>
              </p>
              <div className="flex items-center gap-1.5 mt-1" data-id="element-227">
                <span className="text-xs font-bold text-brand-gold" data-id="element-228">
                  {toast.product.priceText}
                </span>
                <span className="text-[10px] text-brand-green flex items-center gap-0.5" data-id="element-229">
                  <BadgeCheck className="w-3 h-3" data-id="element-230" />
                  Verificado
                </span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}